<script>
import { useAlert } from 'dashboard/composables';

export default {
  name: "CreateEVoucherModal",
  props: {
    treatmentPlans: {
      type: Array,
      default: () => [
        "Facial Rejuvenation Therapy",
        "Full Body Massage",
        "Teeth Whitening Treatment",
      ],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: {
        treatmentPlan: "",
        category: "",
        value: "",
        templateMessage: "",
        isActive: true,
        image: null,
      },
      isSubmitting: false,
      existingImageUrl: null,
      deleteImage: false,
      previewUrl: null,
      URL: window.URL || window.webkitURL,
    };
  },
  watch: {
     edit: {
      immediate: true,
      handler(newVal) {
        if (this.isEdit && newVal) {
          this.form = {
            treatmentPlan: newVal.treatmentPlan || "",
            category: newVal.category || "",
            value: newVal.value || "",
            templateMessage: newVal.template_message || "",
            isActive: newVal.is_active ?? true,
            image: null,
          };

          this.existingImageUrl = newVal.presigned_image_url || null;
        }
      },
    },
  },
  methods: {
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.form.image = file;
        this.deleteImage = false;
        this.previewUrl = this.URL.createObjectURL(file);
      }
    },
    handleRemoveImage() {
      this.form.image = null;
      this.previewUrl = null;
      this.existingImageUrl = null;
      this.deleteImage = true;
    },
    async handleSubmit() {
      if (!this.form.category || !this.form.value) {
        alert("Please fill in all required fields.");
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = new FormData();
        formData.append("category", String(this.form.category));
        formData.append("value", String(this.form.value));
        formData.append("template_message", this.form.templateMessage || "");
        formData.append("is_active", this.form.isActive ? "true" : "false");

        if (this.form.image) {
          formData.append("image", this.form.image);
        }

        if (this.isEdit && this.deleteImage) {
          formData.append("delete_image", "true");
        }

        if (this.isEdit) {
          await this.$store.dispatch("vouchers/updateVoucher", {
            voucherId: this.edit.id,
            voucherData: formData,
          });
          useAlert("Voucher edited successfully!");
        } else {
          await this.$store.dispatch("vouchers/createVoucher", formData);
          useAlert("Voucher created successfully!");
        }

        this.$emit("refresh");
        this.$emit("close");
      } catch (err) {
        console.error(err);
        useAlert("Failed to create voucher");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg w-96 transition-all"
    >
      <!-- Header -->
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ isEdit ? "Edit E-Voucher" : "Create E-Voucher" }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="secondary"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <!-- Form -->
      <div class="flex flex-col gap-4">
        <!-- Category -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            E-Voucher Category
          </label>
          <input
            v-model="form.category"
            type="text"
            placeholder="e.g. Beauty & Wellness"
            class="w-full border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          />
        </div>

        <!-- Value -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            E-Voucher Value
          </label>
          <input
            v-model="form.value"
            type="text"
            placeholder="Enter voucher value"
            class="w-full border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          />
        </div>

        <!-- Template Message -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Template Message
          </label>
          <textarea
            v-model="form.templateMessage"
            rows="3"
            placeholder="Enter template message"
            class="w-full border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-woot-500 focus:border-transparent resize-none"
          ></textarea>
        </div>

        <!-- Image Upload -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Upload Image (optional)
          </label>

          <input
            type="file"
            accept="image/*"
            @change="handleImageUpload"
            class="w-full text-sm text-slate-700 dark:text-slate-300"
          />

          <!-- Preview newly selected image -->
          <div v-if="form.image" class="mt-3">
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">Preview:</p>
            <img
              :src="previewUrl"
              class="w-32 h-32 object-cover rounded border border-slate-300 dark:border-slate-600"
            />
            <button
              class="mt-2 text-sm text-red-600 hover:underline"
              @click="handleRemoveImage"
            >
              Remove Image
            </button>
          </div>

          <!-- Preview existing image when editing -->
          <div v-else-if="existingImageUrl" class="mt-3">
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">Current:</p>
            <img
              :src="existingImageUrl"
              class="w-32 h-32 object-cover rounded border border-slate-300 dark:border-slate-600"
            />
            <button
              class="mt-2 text-sm text-red-600 hover:underline"
              @click="handleRemoveImage"
            >
              Remove Image
            </button>
          </div>
        </div>

        <!-- Treatment Plan -->
        <!-- <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Treatment Plan
          </label>
          <select
            v-model="form.treatmentPlan"
            class="w-full border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          >
            <option value="">Select a treatment</option>
            <option
              v-for="plan in treatmentPlans"
              :key="plan"
              :value="plan"
            >
              {{ plan }}
            </option>
          </select>
        </div> -->
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-4 mt-6">
        <woot-button color-scheme="secondary" @click="$emit('close')">
          Cancel
        </woot-button>
        <woot-button color-scheme="primary" @click="handleSubmit">
          {{ isEdit ? "Update" : "Create" }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
